export * from "./primitives";

// Composite widgets — moved from apps/main as Phase 1D of the
// external-consumption refactor. TokenSelector stays in apps/main
// (pulls Jupiter SDK); BuyWidget accepts it as an optional
// `TokenSelectorComponent` prop so external (sim-only) consumers
// render without it.
export { BuyWidget } from "./BuyWidget";
export { TradeWidget } from "./TradeWidget";
export { TransactionCTA } from "./TransactionCTA";
export * from "./usdv-migration";

// Simulated pointer overlay — the shared cursor primitive every sim
// driver wires into to visually travel to each scripted interaction.
export { useSimCursor } from "./sim-cursor/SimCursor";
export type { SimCursorController, SimCursorType, SimCursorMoveOptions } from "./sim-cursor/SimCursor";

// Enable YaaS — the real "Enable Rewards" form (YaasPositionForm) +
// its verify-wallet states, hoisted from apps/main so external sim
// consumers can render the actual enrollment surface. YaasEnableRewardsSim
// is the drop-in sim driver that auto-plays the verify sequence under
// <SimModeProvider scenario>. apps/main re-exports the form subtree from
// its old paths via shims.
export { YaasPositionForm } from "./yaas/YaasPositionForm";
export { YaasEnableRewardsSim } from "./yaas/YaasEnableRewardsSim";
export type { YaasEnableRewardsSimProps } from "./yaas/YaasEnableRewardsSim";
// Scripted single-interaction driver — opens on the Enable form, performs
// one field change (destination / Claim mode), then submits. The building
// block for the per-action showcase chunks.
export { YaasEnableRewardsInteractionSim } from "./yaas/YaasEnableRewardsInteractionSim";
export type { YaasEnableRewardsInteractionSimProps } from "./yaas/YaasEnableRewardsInteractionSim";

// Enable Rewards "Start Earning" preview frame — the connected,
// no-YaaS-yet right column of the retail landing, composed SDK-free.
// The first frame YaasEnableRewardsSim opens on before advancing into
// the form. Built from the lifted YaasEarnProjectionCTA (balance card
// + projection + CTAs) plus the shared YaasPositionsFooter.
export { YaasEnableRewardsPreview } from "./yaas/YaasEnableRewardsPreview";
export type { YaasEnableRewardsPreviewProps } from "./yaas/YaasEnableRewardsPreview";
export { YaasEarnProjectionCTA } from "./yaas/YaasEarnProjectionCTA";

// Positions panel — the steady-state success end-state the Enable
// Rewards sim crossfades into on submit. Hoisted from apps/main
// (pages/Yaas/components) with its display-only sub-parts
// (YaasPositionCard, YaasPositionsFooter, SectionHeading); all
// mechanism-only / SDK-free. apps/main re-exports the subtree from
// its old paths via shims.
export { PositionsPanel } from "./yaas/PositionsPanel";
export type { PositionsPanelBreakdownRow } from "./yaas/PositionsPanel";
export { YaasPositionCard } from "./yaas/YaasPositionCard";
export { YaasPositionsFooter } from "./yaas/YaasPositionsFooter";
export { SectionHeading } from "./yaas/SectionHeading";

// YaaS live-earnings card — the active-position hero (embossed USDv
// coin, "You've earned (X% APY)" eyebrow, cumulative-earned hero
// number, Next Distribution countdown + bar, live Pending ticker +
// mode label). Hoisted from apps/main (pages/Yaas/sections) SDK-free:
// the pending ticker is DI'd. PendingEarningsTicker is the
// presentational ticker (NumberFlow count-up); apps/main injects its
// useDistributions-backed RetailPendingEarningsTicker. YaasEarningsSim
// is the drop-in sim driver that auto-plays the pending accrual.
export { PortfolioSummary } from "./yaas/PortfolioSummary";
export type { PortfolioSummaryProps } from "./yaas/PortfolioSummary";
export { PendingEarningsTicker } from "./yaas/PendingEarningsTicker";
export type { PendingEarningsTickerProps } from "./yaas/PendingEarningsTicker";
export { YaasEarningsSim } from "./yaas/YaasEarningsSim";
export type { YaasEarningsSimProps } from "./yaas/YaasEarningsSim";

// YaaS "+New Program" doc blocks — the bare, auto-playing program-builder
// sections for the wiki. Each block is a fixture-driven, self-seeded,
// independent section (no wallet / RPC / orgApiFetch) that auto-plays a
// scripted sequence and loops. Configuration is the FIRST block; the other
// four (Policies, Funding, Adding, Viewing) follow the same shape.
// `useSimScript` is the reusable auto-play primitive every block shares.
export type { FramableProps } from "./yaas/programs/framable";
export { ProgramConfiguration } from "./yaas/programs/ProgramConfiguration";
export type {
    ProgramConfigurationProps,
    ProgramConfigControl,
    ProgramConfigDemo,
    ProgramConfigDemoPhase
} from "./yaas/programs/ProgramConfiguration";
export { ProgramConfigurationSim } from "./yaas/programs/ProgramConfigurationSim";
export type { ProgramConfigurationSimProps } from "./yaas/programs/ProgramConfigurationSim";
// Policies is the SECOND block — the props-only View + its auto-play driver,
// hoisted with the whole policy-editor subtree (PolicyCreator / PolicyRow /
// PolicyConfiguration + tranche/wallet editors). The app keeps re-export
// shims at every old path so yaas-client compiles unchanged.
export { ProgramPolicies } from "./yaas/programs/ProgramPolicies";
export type {
    ProgramPoliciesProps,
    ProgramPoliciesProgram,
    ProgramPoliciesDemo
} from "./yaas/programs/ProgramPolicies";
export { ProgramPoliciesSim } from "./yaas/programs/ProgramPoliciesSim";
export type { ProgramPoliciesSimProps } from "./yaas/programs/ProgramPoliciesSim";
// Funding is no longer a separate block/section — it's DERIVED from the Budget
// Type and shown as a read-only field inside the Configuration section (block
// 1). The former Earnings / External toggle View + its sim driver were removed.
export type { FundingAccount, FundingAsset } from "./yaas/programs/fundingFixtures";
// Adding Program is the FOURTH block — a phase chain (not a populate): the
// filled-out draft's confirm surface that auto-plays activation
// (idle → pending → success → loop). The phase is mocked as local fixture
// state; NO real activate mutation / orgApiFetch is pulled in.
// Viewing Program is the FIFTH and LAST block — the settled active read view
// of a created program (active-title header + key stats + budget/timeline
// meters + read-only config summary + read-only policies list + Qualified
// Users table). It reuses ProgramConfiguration's `!creation` summary path and
// ProgramPolicies' read path from blocks 1 & 2, and adds the hoisted read-view
// Views: ProgramActiveHeader (active title block), NextDistributionMeter (the
// live-countdown meter — the only motion), MeterBarWidget, ProgramKeyStats,
// ProgramProgress, QualifiedUsers (+ ParticipantRow, Table/TableRow). All are
// props-only post-split; the app keeps re-export shims at the old paths so
// yaas-client compiles unchanged (its Containers stay app-side). Unlike the
// other four blocks it needs no useSimScript — the ticking countdown is the
// auto-play.
export { ProgramViewSim } from "./yaas/programs/ProgramViewSim";
export type { ProgramViewSimProps } from "./yaas/programs/ProgramViewSim";
// Program Activation is the SIXTH block — the transition from a filled-out
// Draft into the program's read view as it goes Pending → Active. A cursor
// clicks "Add Program", the block swaps to the read view reading "Pending",
// then flips to "Active" after a wait, then loops. The phase is mocked as
// local fixture state (no real activate mutation); the read view reuses the
// "Viewing Program" composition seeded from the same values.
export { ProgramActivationSim } from "./yaas/programs/ProgramActivationSim";
export type { ProgramActivationSimProps } from "./yaas/programs/ProgramActivationSim";
// Neobank scenario — three fixture-driven blocks demonstrating a neobank
// configuring TWO programs in PMP end-to-end, then the Programs page showing
// both Active. Sim A (Continuous Balance Rewards) + Sim B (Deposit Campaign)
// are cursor-driven Configuration → Policies → Activation walkthroughs; the
// Outcome block is the settled Programs rail with both rows Active. All reuse
// the same props-only program-builder Views + fixtures (no wallet / RPC /
// orgApiFetch). See neobankFixtures for the neobank-case → control mapping +
// the documented gaps (revenue split, KYC, one-time bonus).
export { NeobankContinuousProgramSim } from "./yaas/programs/NeobankContinuousProgramSim";
export type { NeobankContinuousProgramSimProps } from "./yaas/programs/NeobankContinuousProgramSim";
export { NeobankDepositCampaignProgramSim } from "./yaas/programs/NeobankDepositCampaignProgramSim";
export type { NeobankDepositCampaignProgramSimProps } from "./yaas/programs/NeobankDepositCampaignProgramSim";
export { NeobankProgramsOutcomeSim } from "./yaas/programs/NeobankProgramsOutcomeSim";
export type { NeobankProgramsOutcomeSimProps } from "./yaas/programs/NeobankProgramsOutcomeSim";
export { NeobankProgramsMobileSim } from "./yaas/programs/NeobankProgramsMobileSim";
export type { NeobankProgramsMobileSimProps } from "./yaas/programs/NeobankProgramsMobileSim";
export { NB_ACTIVE_PROGRAMS, NB_A_PROGRAM_NAME, NB_B_PROGRAM_NAME } from "./yaas/programs/neobankFixtures";
export type { NeobankProgramRow } from "./yaas/programs/neobankFixtures";
export { ProgramActiveHeader } from "./yaas/programs/ProgramActiveHeader";
export type { ProgramActiveHeaderProps, HeaderTag } from "./yaas/programs/ProgramActiveHeader";
export { NextDistributionMeter } from "./yaas/programs/NextDistributionMeter";
export type { NextDistributionMeterProps, NextDistribution } from "./yaas/programs/NextDistributionMeter";
export { MeterBarWidget } from "./yaas/programs/MeterBarWidget";
export type { MeterBarWidgetProps } from "./yaas/programs/MeterBarWidget";
export { ProgramKeyStats, KeyStatsRow } from "./yaas/programs/ProgramKeyStats";
export type { ProgramKeyStatsProps, KeyStat } from "./yaas/programs/ProgramKeyStats";
export { ProgramProgress } from "./yaas/programs/ProgramProgress";
export type { ProgramProgressProps, ProgramMeter } from "./yaas/programs/ProgramProgress";
export { QualifiedUsers } from "./yaas/programs/QualifiedUsers";
export type { QualifiedUsersProps, QualifiedUsersDemo } from "./yaas/programs/QualifiedUsers";
export { ParticipantRow } from "./yaas/programs/ParticipantRow";
export type { ParticipantRowProps, Participant, EligibilityStatus } from "./yaas/programs/ParticipantRow";
export { Table, useTableConfig, columnClassName } from "./yaas/programs/Table";
export type { TableColumn, TableProps } from "./yaas/programs/Table";
export { TableRow } from "./yaas/programs/TableRow";
export type { TableRowProps } from "./yaas/programs/TableRow";
export {
    VIEWING_PROGRAM_NAME,
    VIEWING_STATUS,
    VIEWING_TAGS,
    VIEWING_NEXT_DISTRIBUTION,
    VIEWING_KEY_STATS,
    VIEWING_BUDGET_METER,
    VIEWING_TIMELINE_METER,
    VIEWING_CONFIG,
    VIEWING_POLICIES,
    VIEWING_POLICY_VALUES,
    VIEWING_PARTICIPANTS
} from "./yaas/programs/viewingFixtures";
export { useSimScript } from "./yaas/programs/useSimScript";
export type { SimScriptStep, UseSimScriptOptions } from "./yaas/programs/useSimScript";
export type {
    ProgramConfig,
    ProgramConfigStatus,
    ProgramRegionOption,
    ProgramPeriodOption,
    Policy,
    ConfigurablePolicy,
    SliderPolicy,
    SelectPolicy,
    WalletPolicy,
    TranchePolicy,
    SummaryPolicy
} from "./yaas/programs/types";
export { REGION_OPTIONS, PERIOD_OPTIONS, regionLabelOf } from "./yaas/programs/types";
export {
    POLICY_CATALOG,
    parseTranches,
    serializeTranches,
    parseWalletConfig,
    serializeWalletConfig,
    walletDescription,
    initialValueFor
} from "./yaas/programs/policyFixtures";
export type { Tranche, WalletConfig } from "./yaas/programs/policyFixtures";

// SwapContent is the in-place Swap-mode panel rendered by TradeWidget +
// LegacyTradeWidget. Exposed so LegacyTradeWidget (still in apps/main
// pending Phase 1F) can keep importing it from shared. SwapContentImpl
// is the unmemoized inner function exposed for direct-call tests.
export {
    SwapContent,
    SwapContentImpl,
    getSwapCtaLabel,
    getSwapSuccessLabel,
    getSwapUsdVLabelAmount
} from "./swap/SwapContent";
export type { SwapCtaLabelInputs, SwapSuccessLabelInputs, SwapUsdVLabelAmountInputs } from "./swap/SwapContent";
